import { ArrowRight } from "lucide-react";
import { SLA_TICKER } from "@/content/thoratech";

export default function Hero() {
  const tickerLoop = [...SLA_TICKER, ...SLA_TICKER];

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-[100px] md:pt-[140px] pb-16 md:pb-28 overflow-hidden bg-white"
    >
      {/* Background grid */}
      <div aria-hidden className="absolute inset-0 grid-texture pointer-events-none opacity-60" />
      {/* Subtle accent bar */}
      <div aria-hidden className="absolute left-0 top-[80px] md:top-[96px] h-1 w-1/3 bg-[#2AC0A5]" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left 70% */}
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-3 mb-6 md:mb-8">
              <span className="block h-px w-8 bg-[#2AC0A5]" />
              <span
                data-testid="hero-eyebrow"
                className="text-[10px] md:text-xs font-semibold uppercase tracking-[0.22em] text-[#01080E]"
              >
                Customer Experience | BPO | Outsourced Customer Support | Pune
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display text-[34px] sm:text-5xl lg:text-[80px] leading-[1.05] lg:leading-[1.02] font-extrabold tracking-tight text-[#01080E]"
            >
              We run your customer{" "}
              <span className="relative inline-block">
                operations
                <span aria-hidden className="absolute -bottom-1.5 md:-bottom-2 left-0 right-0 h-1 md:h-[6px] bg-[#2AC0A5]" />
              </span>{" "}
             and own the outcomes.
            </h1>

            <p
              data-testid="hero-subhead"
              className="mt-6 md:mt-8 max-w-2xl text-base md:text-xl leading-relaxed text-[#01080E]/75"
            >
              Thoratech is a CX and BPO managed operations partner for manufacturers and OEMs.
              We manage customer support, lead handling, dealer coordination, service follow-up,
              warranty, AMC and operational reporting from Pune.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.querySelector("#contact");
                  if (el) {
                    const top = el.getBoundingClientRect().top + window.scrollY - 72;
                    window.scrollTo({ top, behavior: "smooth" });
                  }
                }}
                className="group inline-flex items-center gap-2 bg-[#2AC0A5] text-[#01080E] font-semibold px-7 py-4 hover:bg-[#01080E] hover:text-white transition-colors duration-200"
              >
                Book a free process audit
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#services"
                data-testid="hero-cta-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.querySelector("#services");
                  if (el) {
                    const top = el.getBoundingClientRect().top + window.scrollY - 72;
                    window.scrollTo({ top, behavior: "smooth" });
                  }
                }}
                className="inline-flex items-center gap-2 px-7 py-4 font-semibold text-[#01080E] border-b-2 border-[#01080E] hover:text-[#2AC0A5] hover:border-[#2AC0A5] transition-colors duration-200"
              >
                See what we manage
              </a>
            </div>
          </div>

          {/* Right 30%: SLA ticker / image */}
          <div className="lg:col-span-4">
            <div className="relative bg-[#01080E] text-white p-6 md:p-8 overflow-hidden">
              <div aria-hidden className="absolute inset-0 grid-texture-dark opacity-50" />
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#7BDCB5]">
                    Live Governance Stream
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-[#2AC0A5] animate-pulse" />
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/70">
                      Active
                    </span>
                  </span>
                </div>

                <div className="relative h-[280px] overflow-hidden">
                  <div className="ticker-track flex flex-col gap-3">
                    {tickerLoop.map((row, i) => (
                      <div
                        key={i}
                        data-testid={`ticker-row-${i}`}
                        className="flex items-center justify-between border-b border-white/10 pb-3"
                      >
                        <span className="text-sm text-white/80">{row.k}</span>
                        <span className="font-mono-tech text-xs font-semibold tracking-[0.15em] text-[#2AC0A5]">
                          {row.v}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#01080E] to-transparent"
                  />
                </div>

                <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50">
                    Engagement model
                  </span>
                  <span className="font-mono-tech text-xs font-semibold text-white">SLA-DRIVEN</span>
                </div>
              </div>
            </div>

            <p className="mt-4 text-xs text-[#01080E]/55 leading-relaxed">
              Indicative of our standard operating rhythm. Specific SLAs are defined per engagement.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
